import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, getDocs, onSnapshot } from "firebase/firestore";
import { auth, db } from "../firebase";
import { DEFAULT_ACCESS_SETTINGS, canRoleAccessPage } from "../utils/pageAccess";

const AuthContext = createContext(null);

const parseUserStations = (data) => {
  if (Array.isArray(data.stations)) return data.stations;
  if (typeof data.stations === "string") {
    try { return JSON.parse(data.stations); } catch { return []; }
  }
  if (typeof data.station === "string") return [data.station];
  return [];
};

export const AuthProvider = ({ children }) => {
  const [currentUser,   setCurrentUser]   = useState(null);
  const [userRole,      setUserRole]      = useState(null);
  const [userName,      setUserName]      = useState(null);
  const [userStations,  setUserStations]  = useState([]);
  const [allStations,   setAllStations]   = useState([]);
  const [authLoading,   setAuthLoading]   = useState(true);
  const [accessSettings,  setAccessSettings]  = useState(DEFAULT_ACCESS_SETTINGS);
  const [settingsLoading, setSettingsLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (!user) {
        sessionStorage.clear();
        setUserRole(null);
        setUserName(null);
        setUserStations([]);
        setAllStations([]);
        setAuthLoading(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const data = snap.exists() ? snap.data() : {};
        const role = data.role || "viewer";
        setUserRole(role);
        setUserName(data.name || null);
        setUserStations(parseUserStations(data));

        if (role === "superadmin") {
          const s = await getDocs(collection(db, "station_data"));
          const stations = [...new Set(s.docs.map(d => d.data().station).filter(Boolean))].sort();
          setAllStations(stations);
        }
      } catch {
        setUserRole("viewer");
        setUserName(null);
        setUserStations([]);
      } finally {
        setAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  /* Superadmin-controlled page access + backlog settings, kept live so changes apply without a reload. */
  useEffect(() => {
    if (!currentUser) { setAccessSettings(DEFAULT_ACCESS_SETTINGS); setSettingsLoading(false); return; }
    setSettingsLoading(true);
    const unsub = onSnapshot(
      doc(db, "app_settings", "access"),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        setAccessSettings({
          disabledPages: { ...DEFAULT_ACCESS_SETTINGS.disabledPages, ...(data.disabledPages || {}) },
          userOverrides: data.userOverrides || {},
          adminBacklogEntry: data.adminBacklogEntry === true,
        });
        setSettingsLoading(false);
      },
      () => { setAccessSettings(DEFAULT_ACCESS_SETTINGS); setSettingsLoading(false); }
    );
    return () => unsub();
  }, [currentUser]);

  /* Stations a user is allowed to pick from: all of them for superadmin, their own assignment otherwise. */
  const accessibleStations = useMemo(
    () => (userRole === "superadmin" ? allStations : userStations),
    [userRole, allStations, userStations]
  );

  const userFirstName = useMemo(
    () => (userName ? userName.trim().split(/\s+/)[0] : null),
    [userName]
  );

  const value = useMemo(() => ({
    currentUser,
    userRole,
    userName,
    userFirstName,
    userStations,
    allStations,
    accessibleStations,
    authLoading,
    isAdmin: userRole === "admin" || userRole === "superadmin",
    isSuperAdmin: userRole === "superadmin",
    accessSettings,
    settingsLoading,
    canAccessPage: (path) => canRoleAccessPage(userRole, path, accessSettings, currentUser?.uid),
    canEnterBacklog: userRole === "superadmin" || (userRole === "admin" && accessSettings.adminBacklogEntry),
  }), [currentUser, userRole, userName, userFirstName, userStations, allStations, accessibleStations, authLoading, accessSettings, settingsLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
